import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { AuthenticationService } from '../../services/auth/authentication.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { ArbolService } from '@global-service/arbol/arbol.service';
import { ConfiguracionService } from '@app/services/configuracion';
import { firstValueFrom, forkJoin, of, Subject } from 'rxjs';
import * as Sentry from "@sentry/angular";
import { catchError, takeUntil } from 'rxjs/operators';
import {NavegacionService} from "@app/services/navegacion/navegacion.service";
import {EventosCompartidosService} from "@app/services/eventosCompartidos/eventosCompartidos.service";

@Component({ standalone: false, selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit, OnDestroy {
  public email: string;
  public password: string;
  public dominio: string;
  public loading: boolean;
  private readonly activatedRoute: ActivatedRoute;
  private readonly destroy$ = new Subject<void>();

  constructor(
    public authService: AuthenticationService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly toastr: ToastrService,
    private readonly translateService: TranslateService,
    private readonly cdref: ChangeDetectorRef,
    private readonly arbolService: ArbolService,
    private readonly configuracionService: ConfiguracionService,
    private readonly navegacionService: NavegacionService,
    private readonly eventosCompartidos: EventosCompartidosService
  ) {
    this.activatedRoute = route;
    this.email = '';
    this.password = '';
    this.dominio = '';
    this.loading = false;
    this.activatedRoute.url.pipe(takeUntil(this.destroy$)).subscribe(([urlFirst]) => {
      const currentPath = urlFirst?.path;
      if (currentPath === 'logout') {
        this.authService.logout();
        return;
      }
      if (currentPath === 'login') {
        this.authService.clear();
      }
    });
  }

  ngOnInit() {
    this.authService.clear();
    this.configuracionService.clearConfiguraciones();
  }

  async doLogin() {
    if (this.loading) {
      return;
    }
    this.loading = true;
    const myEmail = this.email?.trim().toUpperCase() ?? '';
    const mydominio = this.dominio?.trim().toLowerCase() ?? '';
    const puedeOmitirDominio = myEmail === 'DEIPE@DEIPE.COM' || myEmail === 'SOPORTE@DEIPE.COM';

    if (!this.isLoginDataValid(myEmail, mydominio, puedeOmitirDominio)) {
      this.resetLoginWithWarning();
      return;
    }

    try {
      const response: any = await firstValueFrom(
        this.authService.login(myEmail, this.password, mydominio)
      );
      this.storeLoginResponse(response, myEmail, mydominio);
      this.loginAndStoreTokensNoWait();
      await this.loadConfigurations(mydominio);
      this.authService.isLoggedIn = true;
      await this.navigateAfterLogin(response);
      this.loading = false;
    } catch (error: any) {
      this.handleLoginError(error);
    }
  }

  private isLoginDataValid(email: string, dominio: string, puedeOmitirDominio: boolean): boolean {
    return !!email && !!this.password && (!!dominio || puedeOmitirDominio);
  }

  private resetLoginWithWarning(): void {
    this.loading = false;
    this.toastr.warning('Completa el usuario, la contraseña y el dominio.');
  }

  private storeLoginResponse(response: any, email: string, dominio: string): void {
    Sentry.setUser({
      id: response['user']?.usuario || 'desconocido',
      email: email || 'desconocido',
      login_usuario: response['user']?.login_usuario || 'desconocido',
      username: response['user']?.nombre_usuario || 'desconocido',
      dominio: dominio || 'desconocido'
    });

    localStorage.setItem('dominio', this.dominio);
    localStorage.setItem('perfil', response.user.perfil ?? 'usuario');
    localStorage.setItem('current_user', JSON.stringify(response['user']));
    localStorage.setItem('access_token', response['token']);
  }

  private async loadConfigurations(dominio: string): Promise<void> {
    if (!dominio) return;
    const configurations: any = await firstValueFrom(this.authService.getConfigurations());
    if (!configurations) return;

    localStorage.setItem('permisos', JSON.stringify(configurations.Permiso || configurations));
    if (configurations.Configuracion) {
      localStorage.setItem('configuraciones', JSON.stringify(configurations.Configuracion));
    }
  }

  private async navigateAfterLogin(response: any): Promise<void> {
    const userPerfil = String(response?.user?.perfil ?? '').toLowerCase();
    if (userPerfil === 'soporte') {
      this.eventosCompartidos.setEnPaginaClientesBackOffice(false);
      await this.navegacionService.navegar('BackOffice');
      return;
    }

    this.arbolService.initArbolGrupo();
    await this.arbolService.init();
    await this.router.navigate(['dashboard']);
  }

  private handleLoginError(error: any): void {
    this.authService.isLoggedIn = false;
    this.loading = false;
    const isAuthError = !!error?.status && error.status >= 400 && error.status < 500;
    const translationKey = isAuthError ? 'credenciales erroneos' : 'Error al iniciar la aplicación';
    this.translateService.get(translationKey).subscribe((value: any) => {
      this.toastr.warning(value);
    });
  }


  loginAndStoreTokensNoWait = () => {
    forkJoin({
      dl: this.authService.getDLToken(this.email, this.password, this.dominio).pipe(
        catchError(() => of({ access_token: '' }))
      ),
      gw2: this.authService.getGW2Token(this.email, this.password, this.dominio).pipe(
        catchError(() => of({ access_token: '' }))
      ),
    }).subscribe(({ dl, gw2 }) => {
      localStorage.setItem('dlToken', dl?.access_token ?? '');
      localStorage.setItem('gw2Token', gw2?.access_token ?? '');
    });

  }
  onChangeLanguage(data?: any) {}

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
